import { useEffect, useRef, useState } from 'react';

// Número WhatsApp no formato internacional (sem + ou espaços)
const WA_NUMBER = '5547984929295';

// Áreas de interesse — correspondência exata com os títulos da seção "Áreas de Atuação"
const AREAS_DE_INTERESSE = [
  'Divórcios',
  'Guarda e Convivência',
  'Pensão Alimentícia',
  'Paternidade',
  'Inventários e Testamentos',
  'Partilhas Patrimoniais',
];

const WA_LINK = `https://wa.me/${WA_NUMBER}?text=Ol%C3%A1%2C%20gostaria%20de%20falar%20com%20a%20advogada.`;

export default function Contact() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  /**
   * Ao clicar em "Enviar":
   * 1. Captura os valores dos campos
   * 2. Monta a mensagem no formato exato solicitado
   * 3. Codifica com encodeURIComponent
   * 4. Abre o WhatsApp em nova aba com a mensagem pronta
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Impede o comportamento padrão do formulário (sem reload, sem POST)
    e.preventDefault();

    const form = e.currentTarget;

    // Captura os valores de cada campo
    const nome = (form.elements.namedItem('nome') as HTMLInputElement).value.trim();
    const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim();
    const area = (form.elements.namedItem('area') as HTMLSelectElement).value;
    const mensagem = (form.elements.namedItem('mensagem') as HTMLTextAreaElement).value.trim();

    // Valida limite de caracteres da mensagem
    if (mensagem.length > MAX_CHARS) return;

    // Monta a mensagem no formato exato especificado
    const texto =
      `Nome Completo: ${nome} | E-mail: ${email} | Área de Interesse: ${area} | Motivo do Contato: ${mensagem}`;

    // Codifica para URL e abre o WhatsApp em nova aba
    const textoCodificado = encodeURIComponent(texto);
    window.open(`https://wa.me/${WA_NUMBER}?text=${textoCodificado}`, '_blank', 'noopener,noreferrer');
  };

  const inputClass =
    'w-full bg-white border border-border-light text-body-gray placeholder-body-gray/40 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-chestnut transition-colors duration-200';

  return (
    <section id="contato" ref={ref} className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-10 bg-chestnut"></div>
            <span className="text-chestnut text-xs tracking-[0.2em] uppercase font-sans font-medium">Fale Conosco</span>
            <div className="h-px w-10 bg-chestnut"></div>
          </div>
          <h2 className="font-serif text-ink-black text-3xl md:text-4xl font-semibold mb-4">
            Entre em Contato
          </h2>
          <p className="text-body-gray font-sans max-w-xl mx-auto text-base">
            O primeiro passo para a solução começa com uma conversa.
          </p>
        </div>

        {/* Formulário — largura total */}
        <div
          className={`transition-all duration-700 mb-6 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="bg-parchment rounded-2xl p-8 lg:p-10">
            <h3 className="font-serif text-ink-black text-xl font-semibold mb-6">Envie uma mensagem</h3>

            <form
              id="contato-whatsapp"
              onSubmit={handleSubmit}
              className="grid sm:grid-cols-2 gap-4"
            >
              {/* Campo: Nome Completo */}
              <div>
                <label className="block text-xs text-body-gray font-sans font-medium mb-1.5">
                  Nome Completo *
                </label>
                <input
                  name="nome"
                  type="text"
                  required
                  placeholder="Seu nome completo"
                  className={inputClass}
                />
              </div>

              {/* Campo: E-mail */}
              <div>
                <label className="block text-xs text-body-gray font-sans font-medium mb-1.5">
                  E-mail *
                </label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="seu@email.com"
                  className={inputClass}
                />
              </div>

              {/* Campo: Área de Interesse (select com opções da seção "Atuação") */}
              <div className="sm:col-span-2">
                <label className="block text-xs text-body-gray font-sans font-medium mb-1.5">
                  Área de Interesse *
                </label>
                <select
                  name="area"
                  required
                  defaultValue=""
                  className={`${inputClass} appearance-none cursor-pointer`}
                >
                  <option value="" disabled>Selecione uma área...</option>
                  {AREAS_DE_INTERESSE.map((area) => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>

              {/* Campo: Mensagem */}
              <div className="sm:col-span-2">
                <label className="block text-xs text-body-gray font-sans font-medium mb-1.5">
                  Mensagem *&nbsp;
                  <span className={`text-xs ${charCount > MAX_CHARS ? 'text-red-500' : 'text-body-gray/40'}`}>
                    {charCount}/{MAX_CHARS}
                  </span>
                </label>
                <textarea
                  name="mensagem"
                  required
                  rows={4}
                  maxLength={MAX_CHARS}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  placeholder="Conte brevemente sua situação..."
                  className={`${inputClass} resize-none`}
                />
                {charCount > MAX_CHARS && (
                  <p className="text-red-500 text-xs mt-1 font-sans">Limite de 500 caracteres atingido.</p>
                )}
              </div>

              {/* Botão de envio — abre WhatsApp com mensagem pronta */}
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={charCount > MAX_CHARS}
                  className="w-full flex items-center justify-center gap-2 bg-burnt-peach hover:bg-burnt-peach-light disabled:opacity-60 text-white font-sans font-medium py-3.5 rounded-full transition-all duration-200 whitespace-nowrap text-sm cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  Enviar pelo WhatsApp
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Info cards — horizontal row */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-3 gap-4 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* WhatsApp */}
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-4 bg-parchment rounded-xl p-5 hover:bg-parchment-dark transition-colors duration-200 cursor-pointer group"
          >
            <div className="w-11 h-11 flex items-center justify-center bg-[#25D366]/12 rounded-xl flex-shrink-0">
              <i className="ri-whatsapp-line text-[#25D366] text-xl"></i>
            </div>
            <div>
              <p className="text-xs text-body-gray font-sans font-medium mb-0.5">WhatsApp</p>
              <p className="text-ink-black font-sans font-semibold text-sm group-hover:text-chestnut transition-colors">+55 47 98492-9295</p>
            </div>
          </a>

          {/* Location */}
          <div className="flex items-center gap-4 bg-parchment rounded-xl p-5">
            <div className="w-11 h-11 flex items-center justify-center bg-burnt-peach/15 rounded-xl flex-shrink-0">
              <i className="ri-map-pin-2-line text-burnt-peach text-xl"></i>
            </div>
            <div>
              <p className="text-xs text-body-gray font-sans font-medium mb-0.5">Atendimento Presencial</p>
              <p className="text-ink-black font-sans font-semibold text-sm">Blumenau e Porto Alegre</p>
            </div>
          </div>

          {/* Instagram */}
          <a
            href="https://www.instagram.com/adv.leidegoncalves/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-4 bg-parchment rounded-xl p-5 hover:bg-parchment-dark transition-colors duration-200 cursor-pointer group"
          >
            <div className="w-11 h-11 flex items-center justify-center bg-burnt-peach/15 rounded-xl flex-shrink-0">
              <i className="ri-instagram-line text-burnt-peach text-xl"></i>
            </div>
            <div>
              <p className="text-xs text-body-gray font-sans font-medium mb-0.5">Instagram</p>
              <p className="text-ink-black font-sans font-semibold text-sm group-hover:text-chestnut transition-colors">@adv.leidegoncalves</p>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
